import React, { useState, useEffect } from 'react';
import { useAuth } from '../lib/auth';
import { api } from '../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { PlayCircle, StopCircle, AlertTriangle, Package, Settings } from 'lucide-react';

const Jobs = () => {
  const { user, hasRole } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [coilIns, setCoilIns] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [dies, setDies] = useState([]);
  const [activeTab, setActiveTab] = useState('active');
  const [startJobOpen, setStartJobOpen] = useState(false);
  const [activeJobDialog, setActiveJobDialog] = useState(null);
  const [dialogType, setDialogType] = useState(null);
  const [loading, setLoading] = useState(false);

  const [startJobData, setStartJobData] = useState({
    coil_in_id: '',
    program_id: '',
    die_id: '',
    start_datetime: new Date().toISOString().slice(0, 16),
    notes: ''
  });

  const [dieChangeData, setDieChangeData] = useState({
    timestamp: new Date().toISOString().slice(0, 16),
    from_die_id: '',
    to_die_id: '',
    reason: ''
  });

  const [issueData, setIssueData] = useState({
    timestamp: new Date().toISOString().slice(0, 16),
    issue_type: 'CRACK',
    notes: ''
  });

  const [outputLotData, setOutputLotData] = useState({
    count: '',
    avg_grams: '',
    total_kg: ''
  });

  const canManageJobs = hasRole(['ADMIN', 'OWNER', 'OPERATOR']);

  useEffect(() => {
    fetchJobs();
    fetchCoilIns();
    fetchDies();
  }, []);

  const fetchJobs = async () => {
    try {
      const response = await api.jobs.list();
      setJobs(response.data);
    } catch (error) {
      toast.error('Failed to fetch jobs');
    }
  };

  const fetchCoilIns = async () => {
    try {
      const response = await api.coilIns.list({ is_active: true });
      setCoilIns(response.data);
    } catch (error) {
      toast.error('Failed to fetch coil entries');
    }
  };

  const fetchDies = async () => {
    try {
      const response = await api.dies.list({ is_active: true });
      setDies(response.data);
    } catch (error) {
      toast.error('Failed to fetch dies');
    }
  };

  const handleCoilChange = async (coilInId) => {
    setStartJobData({ ...startJobData, coil_in_id: coilInId, program_id: '' });
    try {
      const response = await api.programs.list({ coil_in_id: coilInId });
      setPrograms(response.data.filter(p => p.is_active));
    } catch (error) {
      toast.error('Failed to fetch programs');
    }
  };

  const handleStartJob = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.jobs.create({
        ...startJobData,
        start_datetime: new Date(startJobData.start_datetime).toISOString()
      });
      toast.success('Job started successfully');
      setStartJobOpen(false);
      resetStartJobForm();
      fetchJobs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start job');
    } finally {
      setLoading(false);
    }
  };

  const handleDieChange = async (e) => {
    e.preventDefault();
    if (!activeJobDialog) return;
    setLoading(true);
    try {
      await api.jobs.addDieChange(activeJobDialog.id, {
        ...dieChangeData,
        timestamp: new Date(dieChangeData.timestamp).toISOString()
      });
      toast.success('Die change recorded');
      setDialogType(null);
      setActiveJobDialog(null);
      resetDieChangeForm();
      fetchJobs();
    } catch (error) {
      toast.error('Failed to record die change');
    } finally {
      setLoading(false);
    }
  };

  const handleIssue = async (e) => {
    e.preventDefault();
    if (!activeJobDialog) return;
    setLoading(true);
    try {
      await api.jobs.addIssue(activeJobDialog.id, {
        ...issueData,
        timestamp: new Date(issueData.timestamp).toISOString()
      });
      toast.success('Issue recorded');
      setDialogType(null);
      setActiveJobDialog(null);
      resetIssueForm();
      fetchJobs();
    } catch (error) {
      toast.error('Failed to record issue');
    } finally {
      setLoading(false);
    }
  };

  const handleOutputLot = async (e) => {
    e.preventDefault();
    if (!activeJobDialog) return;
    setLoading(true);
    try {
      await api.jobs.addOutputLot(activeJobDialog.id, {
        count: parseInt(outputLotData.count),
        avg_grams: parseFloat(outputLotData.avg_grams),
        total_kg: parseFloat(outputLotData.total_kg)
      });
      toast.success('Output lot recorded');
      setDialogType(null);
      setActiveJobDialog(null);
      resetOutputLotForm();
      fetchJobs();
    } catch (error) {
      toast.error('Failed to record output lot');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseJob = async (jobId) => {
    if (!window.confirm('Are you sure you want to close this job?')) return;
    setLoading(true);
    try {
      await api.jobs.update(jobId, {
        status: 'COMPLETED',
        end_datetime: new Date().toISOString()
      });
      toast.success('Job closed successfully');
      fetchJobs();
    } catch (error) {
      toast.error('Failed to close job');
    } finally {
      setLoading(false);
    }
  };

  const resetStartJobForm = () => {
    setStartJobData({
      coil_in_id: '',
      program_id: '',
      die_id: '',
      start_datetime: new Date().toISOString().slice(0, 16),
      notes: ''
    });
    setPrograms([]);
  };

  const resetDieChangeForm = () => {
    setDieChangeData({
      timestamp: new Date().toISOString().slice(0, 16),
      from_die_id: '',
      to_die_id: '',
      reason: ''
    });
  };

  const resetIssueForm = () => {
    setIssueData({
      timestamp: new Date().toISOString().slice(0, 16),
      issue_type: 'CRACK',
      notes: ''
    });
  };

  const resetOutputLotForm = () => {
    setOutputLotData({
      count: '',
      avg_grams: '',
      total_kg: ''
    });
  };

  const openDialog = (job, type) => {
    setActiveJobDialog(job);
    setDialogType(type);
    if (type === 'die_change') {
      setDieChangeData({ ...dieChangeData, from_die_id: job.die_id });
    }
  };

  const getStatusLed = (status) => {
    switch (status) {
      case 'IN_PROGRESS':
        return (
          <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase font-bold text-[#10B981]">
            <span className="w-1.5 h-1.5 rounded-full bg-[#10B981] shadow-[0_0_6px_#10B981] inline-block" /> IN PROGRESS
          </div>
        );
      case 'COMPLETED':
        return (
          <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase font-bold text-[#6B7280]">
            <span className="w-1.5 h-1.5 rounded-full bg-[#6B7280] shadow-[0_0_6px_#6B7280] inline-block" /> COMPLETED
          </div>
        );
      case 'PAUSED':
        return (
          <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase font-bold text-[#F59E0B]">
            <span className="w-1.5 h-1.5 rounded-full bg-[#F59E0B] shadow-[0_0_6px_#F59E0B] inline-block" /> PAUSED
          </div>
        );
      default:
        return (
          <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase font-bold text-slate-400">
            <span className="w-1.5 h-1.5 rounded-full bg-slate-500 inline-block" /> {status}
          </div>
        );
    }
  };

  return (
    <div className="space-y-6 bg-[#141414] p-6 text-white min-h-screen" data-testid="jobs-page">
      <div className="flex items-center justify-between border-b border-[#333333] pb-4">
        <div>
          <h1 className="font-mono text-3xl font-bold tracking-wider text-white uppercase">Production Jobs</h1>
          <p className="text-slate-400 font-sans text-xs tracking-wider uppercase mt-1">Manage production workflows</p>
        </div>
        {canManageJobs && (
          <button
            onClick={() => { resetStartJobForm(); setStartJobOpen(true); }}
            data-testid="start-job-button"
            className="bg-[#FF6B00] text-white font-mono uppercase tracking-wider font-bold rounded-none border-b-2 border-black/30 hover:bg-[#CC5600] h-11 px-4 flex items-center justify-center gap-1.5 transition-colors"
          >
            <PlayCircle className="h-4 w-4" />
            Start Job
          </button>
        )}
      </div>

      <div className="space-y-4">
        {/* Sub-nav Tabs */}
        <div className="flex gap-1 border-b border-[#333333] pb-px">
          <button
            type="button"
            onClick={() => setActiveTab('active')}
            className={`h-10 px-6 font-mono text-xs uppercase tracking-wider font-bold rounded-none border-b-2 transition-colors ${
              activeTab === 'active'
                ? 'bg-[#FF6B00] text-white border-[#FF6B00]'
                : 'bg-[#1E1E1E] border-transparent text-slate-400 hover:text-white hover:bg-[#2A2A2A]'
            }`}
          >
            Active Jobs
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('completed')}
            className={`h-10 px-6 font-mono text-xs uppercase tracking-wider font-bold rounded-none border-b-2 transition-colors ${
              activeTab === 'completed'
                ? 'bg-[#FF6B00] text-white border-[#FF6B00]'
                : 'bg-[#1E1E1E] border-transparent text-slate-400 hover:text-white hover:bg-[#2A2A2A]'
            }`}
          >
            Completed Jobs
          </button>
        </div>

        {activeTab === 'active' && (
          <div className="space-y-4 mt-4">
            {jobs.filter(j => j.status === 'IN_PROGRESS').length === 0 ? (
              <div className="bg-[#1E1E1E] border border-[#333333] p-8 text-center font-mono text-xs text-slate-400 rounded-none">
                NO ACTIVE PRODUCTION JOBS RECORDED
              </div>
            ) : (
              jobs.filter(j => j.status === 'IN_PROGRESS').map(job => (
                <div key={job.id} className="bg-[#1E1E1E] border border-[#333333] rounded-none">
                  <div className="p-4 border-b border-[#333333] bg-[#2A2A2A]/40 flex items-center justify-between">
                    <span className="font-mono text-xs font-bold text-white tracking-wider">JOB #{job.id.slice(0, 8)}</span>
                    {getStatusLed(job.status)}
                  </div>
                  <div className="p-4 space-y-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
                      <div>
                        <p className="font-sans text-[10px] uppercase tracking-wider text-slate-400">Operator</p>
                        <p className="font-mono text-white uppercase mt-0.5">{job.operator_name}</p>
                      </div>
                      <div>
                        <p className="font-sans text-[10px] uppercase tracking-wider text-slate-400">Started</p>
                        <p className="font-mono text-white mt-0.5">{new Date(job.start_datetime).toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="font-sans text-[10px] uppercase tracking-wider text-slate-400">Issues</p>
                        <p className="font-mono text-white mt-0.5">{job.issues.length}</p>
                      </div>
                      <div>
                        <p className="font-sans text-[10px] uppercase tracking-wider text-slate-400">Output Lots</p>
                        <p className="font-mono text-white mt-0.5">{job.output_lots.length}</p>
                      </div>
                    </div>

                    {canManageJobs && (
                      <div className="flex gap-2 flex-wrap pt-3 border-t border-[#333333]/40">
                        <button
                          type="button"
                          className="bg-[#2A2A2A] border border-[#333333] hover:bg-[#1E1E1E] text-white rounded-none font-mono text-[10px] uppercase tracking-wider h-9 px-3 flex items-center gap-1.5 transition-colors"
                          onClick={() => openDialog(job, 'die_change')}
                        >
                          <Settings className="h-3.5 w-3.5 text-slate-400" />
                          Die Change
                        </button>
                        <button
                          type="button"
                          className="bg-[#2A2A2A] border border-[#333333] hover:bg-[#1E1E1E] text-white rounded-none font-mono text-[10px] uppercase tracking-wider h-9 px-3 flex items-center gap-1.5 transition-colors"
                          onClick={() => openDialog(job, 'issue')}
                        >
                          <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
                          Report Issue
                        </button>
                        <button
                          type="button"
                          className="bg-[#2A2A2A] border border-[#333333] hover:bg-[#1E1E1E] text-white rounded-none font-mono text-[10px] uppercase tracking-wider h-9 px-3 flex items-center gap-1.5 transition-colors"
                          onClick={() => openDialog(job, 'output')}
                        >
                          <Package className="h-3.5 w-3.5 text-blue-400" />
                          Add Output
                        </button>
                        <button
                          type="button"
                          className="bg-[#FF6B00] hover:bg-[#CC5600] text-white rounded-none font-mono text-[10px] uppercase tracking-wider font-bold h-9 px-3 flex items-center gap-1.5 border-b border-black/30 transition-colors ml-auto"
                          onClick={() => handleCloseJob(job.id)}
                          data-testid={`close-job-${job.id}`}
                        >
                          <StopCircle className="h-3.5 w-3.5" />
                          Close Job
                        </button>
                      </div>
                    )}

                    {job.notes && (
                      <p className="text-xs text-slate-400 font-sans border-t border-[#333333]/30 pt-2 leading-relaxed">Notes: {job.notes}</p>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {activeTab === 'completed' && (
          <div className="space-y-4 mt-4">
            {jobs.filter(j => j.status === 'COMPLETED').length === 0 ? (
              <div className="bg-[#1E1E1E] border border-[#333333] p-8 text-center font-mono text-xs text-slate-400 rounded-none">
                NO COMPLETED JOBS RECORDED
              </div>
            ) : (
              jobs.filter(j => j.status === 'COMPLETED').map(job => (
                <div key={job.id} className="bg-[#1E1E1E] border border-[#333333] rounded-none">
                  <div className="p-4 border-b border-[#333333] bg-[#2A2A2A]/40 flex items-center justify-between">
                    <span className="font-mono text-xs font-bold text-white tracking-wider">JOB #{job.id.slice(0, 8)}</span>
                    {getStatusLed(job.status)}
                  </div>
                  <div className="p-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
                      <div>
                        <p className="font-sans text-[10px] uppercase tracking-wider text-slate-400">Operator</p>
                        <p className="font-mono text-white uppercase mt-0.5">{job.operator_name}</p>
                      </div>
                      <div>
                        <p className="font-sans text-[10px] uppercase tracking-wider text-slate-400">Duration</p>
                        <p className="font-mono text-white mt-0.5">
                          {job.end_datetime ?
                            Math.round((new Date(job.end_datetime) - new Date(job.start_datetime)) / (1000 * 60)) + ' min'
                            : 'N/A'}
                        </p>
                      </div>
                      <div>
                        <p className="font-sans text-[10px] uppercase tracking-wider text-slate-400">Total Output</p>
                        <p className="font-mono text-[#FF6B00] font-bold mt-0.5">
                          {job.output_lots.reduce((sum, lot) => sum + lot.total_kg, 0).toFixed(2)} KG
                        </p>
                      </div>
                      <div>
                        <p className="font-sans text-[10px] uppercase tracking-wider text-slate-400">Pieces</p>
                        <p className="font-mono text-white mt-0.5">
                          {job.output_lots.reduce((sum, lot) => sum + lot.count, 0)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Start Job Dialog */}
      <Dialog open={startJobOpen} onOpenChange={setStartJobOpen}>
        <DialogContent className="max-w-2xl bg-[#1E1E1E] border border-[#333333] rounded-none text-white">
          <DialogHeader className="border-b border-[#333333] pb-3">
            <DialogTitle className="font-mono text-lg uppercase tracking-wider text-white">Start New Job</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleStartJob} className="space-y-4 pt-4">
            <div>
              <label className="font-sans text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1 block">Coil *</label>
              <select
                className="flex h-10 w-full items-center rounded-none border border-[#333333] bg-[#2A2A2A] px-3 py-2 text-xs font-mono text-white focus:outline-none focus:border-[#FF6B00]"
                value={startJobData.coil_in_id}
                onChange={(e) => handleCoilChange(e.target.value)}
                required
                data-testid="job-coil-select"
              >
                <option value="">Select coil</option>
                {coilIns.map(coil => (
                  <option key={coil.id} value={coil.id}>
                    {coil.party_name} - {new Date(coil.inward_datetime).toLocaleDateString()}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="font-sans text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1 block">Program *</label>
              <select
                className="flex h-10 w-full items-center rounded-none border border-[#333333] bg-[#2A2A2A] px-3 py-2 text-xs font-mono text-white focus:outline-none focus:border-[#FF6B00]"
                value={startJobData.program_id}
                onChange={(e) => setStartJobData({ ...startJobData, program_id: e.target.value })}
                required
              >
                <option value="">Select program</option>
                {programs.map(prog => (
                  <option key={prog.id} value={prog.id}>
                    {prog.circle_sizes.join(', ')} mm
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="font-sans text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1 block">Die *</label>
              <select
                className="flex h-10 w-full items-center rounded-none border border-[#333333] bg-[#2A2A2A] px-3 py-2 text-xs font-mono text-white focus:outline-none focus:border-[#FF6B00]"
                value={startJobData.die_id}
                onChange={(e) => setStartJobData({ ...startJobData, die_id: e.target.value })}
                required
              >
                <option value="">Select die</option>
                {dies.map(die => (
                  <option key={die.id} value={die.id}>
                    {die.code} - {die.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="start_datetime" className="font-sans text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1 block">Start Time *</label>
              <input
                id="start_datetime"
                type="datetime-local"
                className="w-full h-10 px-3 bg-[#2A2A2A] border border-[#333333] text-white font-mono rounded-none focus:outline-none focus:border-[#FF6B00] text-sm"
                value={startJobData.start_datetime}
                onChange={(e) => setStartJobData({ ...startJobData, start_datetime: e.target.value })}
                required
              />
            </div>
            <div>
              <label htmlFor="job_notes" className="font-sans text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1 block">Notes</label>
              <textarea
                id="job_notes"
                className="w-full min-h-[80px] p-3 bg-[#2A2A2A] border border-[#333333] text-white font-mono rounded-none focus:outline-none focus:border-[#FF6B00] text-sm"
                value={startJobData.notes}
                onChange={(e) => setStartJobData({ ...startJobData, notes: e.target.value })}
              />
            </div>
            <div className="flex gap-2 justify-end pt-4 border-t border-[#333333]">
              <button
                type="button"
                className="bg-[#2A2A2A] border border-[#333333] hover:bg-[#1E1E1E] text-white rounded-none font-mono text-xs uppercase tracking-wider h-11 px-4 transition-colors"
                onClick={() => setStartJobOpen(false)}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="bg-[#FF6B00] text-white font-mono uppercase tracking-wider font-bold rounded-none border-b-2 border-black/30 hover:bg-[#CC5600] h-11 px-6 flex items-center justify-center gap-1.5 transition-colors"
                data-testid="submit-start-job"
              >
                {loading ? 'Starting...' : 'Start Job'}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Die Change Dialog */}
      <Dialog open={dialogType === 'die_change'} onOpenChange={(open) => !open && setDialogType(null)}>
        <DialogContent className="max-w-md bg-[#1E1E1E] border border-[#333333] rounded-none text-white">
          <DialogHeader className="border-b border-[#333333] pb-3">
            <DialogTitle className="font-mono text-lg uppercase tracking-wider text-white">Record Die Change</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleDieChange} className="space-y-4 pt-4">
            <div>
              <label htmlFor="die_change_time" className="font-sans text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1 block">Timestamp *</label>
              <input
                id="die_change_time"
                type="datetime-local"
                className="w-full h-10 px-3 bg-[#2A2A2A] border border-[#333333] text-white font-mono rounded-none focus:outline-none focus:border-[#FF6B00] text-sm"
                value={dieChangeData.timestamp}
                onChange={(e) => setDieChangeData({ ...dieChangeData, timestamp: e.target.value })}
                required
              />
            </div>
            <div>
              <label className="font-sans text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1 block">To Die *</label>
              <select
                className="flex h-10 w-full items-center rounded-none border border-[#333333] bg-[#2A2A2A] px-3 py-2 text-xs font-mono text-white focus:outline-none focus:border-[#FF6B00]"
                value={dieChangeData.to_die_id}
                onChange={(e) => setDieChangeData({ ...dieChangeData, to_die_id: e.target.value })}
                required
              >
                <option value="">Select new die</option>
                {dies.map(die => (
                  <option key={die.id} value={die.id}>
                    {die.code} - {die.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="die_change_reason" className="font-sans text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1 block">Reason *</label>
              <textarea
                id="die_change_reason"
                className="w-full min-h-[80px] p-3 bg-[#2A2A2A] border border-[#333333] text-white font-mono rounded-none focus:outline-none focus:border-[#FF6B00] text-sm"
                value={dieChangeData.reason}
                onChange={(e) => setDieChangeData({ ...dieChangeData, reason: e.target.value })}
                required
              />
            </div>
            <div className="flex gap-2 justify-end pt-4 border-t border-[#333333]">
              <button
                type="button"
                className="bg-[#2A2A2A] border border-[#333333] hover:bg-[#1E1E1E] text-white rounded-none font-mono text-xs uppercase tracking-wider h-11 px-4 transition-colors"
                onClick={() => setDialogType(null)}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="bg-[#FF6B00] text-white font-mono uppercase tracking-wider font-bold rounded-none border-b-2 border-black/30 hover:bg-[#CC5600] h-11 px-6 flex items-center justify-center gap-1.5 transition-colors"
              >
                {loading ? 'Recording...' : 'Record'}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Issue Dialog */}
      <Dialog open={dialogType === 'issue'} onOpenChange={(open) => !open && setDialogType(null)}>
        <DialogContent className="max-w-md bg-[#1E1E1E] border border-[#333333] rounded-none text-white">
          <DialogHeader className="border-b border-[#333333] pb-3">
            <DialogTitle className="font-mono text-lg uppercase tracking-wider text-white">Report Issue</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleIssue} className="space-y-4 pt-4">
            <div>
              <label htmlFor="issue_time" className="font-sans text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1 block">Timestamp *</label>
              <input
                id="issue_time"
                type="datetime-local"
                className="w-full h-10 px-3 bg-[#2A2A2A] border border-[#333333] text-white font-mono rounded-none focus:outline-none focus:border-[#FF6B00] text-sm"
                value={issueData.timestamp}
                onChange={(e) => setIssueData({ ...issueData, timestamp: e.target.value })}
                required
              />
            </div>
            <div>
              <label className="font-sans text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1 block">Issue Type *</label>
              <select
                className="flex h-10 w-full items-center rounded-none border border-[#333333] bg-[#2A2A2A] px-3 py-2 text-xs font-mono text-white focus:outline-none focus:border-[#FF6B00]"
                value={issueData.issue_type}
                onChange={(e) => setIssueData({ ...issueData, issue_type: e.target.value })}
              >
                <option value="CRACK">Crack</option>
                <option value="RECOVERY">Recovery</option>
                <option value="MACHINE_BREAKDOWN">Machine Breakdown</option>
                <option value="OTHER">Other</option>
              </select>
            </div>
            <div>
              <label htmlFor="issue_notes" className="font-sans text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1 block">Notes *</label>
              <textarea
                id="issue_notes"
                className="w-full min-h-[80px] p-3 bg-[#2A2A2A] border border-[#333333] text-white font-mono rounded-none focus:outline-none focus:border-[#FF6B00] text-sm"
                value={issueData.notes}
                onChange={(e) => setIssueData({ ...issueData, notes: e.target.value })}
                required
              />
            </div>
            <div className="flex gap-2 justify-end pt-4 border-t border-[#333333]">
              <button
                type="button"
                className="bg-[#2A2A2A] border border-[#333333] hover:bg-[#1E1E1E] text-white rounded-none font-mono text-xs uppercase tracking-wider h-11 px-4 transition-colors"
                onClick={() => setDialogType(null)}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="bg-[#FF6B00] text-white font-mono uppercase tracking-wider font-bold rounded-none border-b-2 border-black/30 hover:bg-[#CC5600] h-11 px-6 flex items-center justify-center gap-1.5 transition-colors"
              >
                {loading ? 'Recording...' : 'Record'}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Output Lot Dialog */}
      <Dialog open={dialogType === 'output'} onOpenChange={(open) => !open && setDialogType(null)}>
        <DialogContent className="max-w-md bg-[#1E1E1E] border border-[#333333] rounded-none text-white">
          <DialogHeader className="border-b border-[#333333] pb-3">
            <DialogTitle className="font-mono text-lg uppercase tracking-wider text-white">Add Output Lot</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleOutputLot} className="space-y-4 pt-4">
            <div>
              <label htmlFor="lot_count" className="font-sans text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1 block">Count *</label>
              <input
                id="lot_count"
                type="number"
                className="w-full h-10 px-3 bg-[#2A2A2A] border border-[#333333] text-white font-mono rounded-none focus:outline-none focus:border-[#FF6B00] placeholder-slate-500 text-sm"
                value={outputLotData.count}
                onChange={(e) => setOutputLotData({ ...outputLotData, count: e.target.value })}
                required
              />
            </div>
            <div>
              <label htmlFor="avg_grams" className="font-sans text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1 block">Avg Weight (grams) *</label>
              <input
                id="avg_grams"
                type="number"
                step="0.1"
                className="w-full h-10 px-3 bg-[#2A2A2A] border border-[#333333] text-white font-mono rounded-none focus:outline-none focus:border-[#FF6B00] placeholder-slate-500 text-sm"
                value={outputLotData.avg_grams}
                onChange={(e) => {
                  const avg = e.target.value;
                  setOutputLotData({
                    ...outputLotData,
                    avg_grams: avg,
                    total_kg: outputLotData.count && avg ? (outputLotData.count * avg / 1000).toFixed(2) : ''
                  });
                }}
                required
              />
            </div>
            <div>
              <label htmlFor="total_kg" className="font-sans text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1 block">Total Weight (kg)</label>
              <input
                id="total_kg"
                type="number"
                step="0.01"
                className="w-full h-10 px-3 bg-[#1E1E1E] border border-[#333333]/50 text-slate-400 font-mono rounded-none focus:outline-none text-sm cursor-not-allowed"
                value={outputLotData.total_kg}
                readOnly
              />
            </div>
            <div className="flex gap-2 justify-end pt-4 border-t border-[#333333]">
              <button
                type="button"
                className="bg-[#2A2A2A] border border-[#333333] hover:bg-[#1E1E1E] text-white rounded-none font-mono text-xs uppercase tracking-wider h-11 px-4 transition-colors"
                onClick={() => setDialogType(null)}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="bg-[#FF6B00] text-white font-mono uppercase tracking-wider font-bold rounded-none border-b-2 border-black/30 hover:bg-[#CC5600] h-11 px-6 flex items-center justify-center gap-1.5 transition-colors"
              >
                {loading ? 'Recording...' : 'Record'}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Jobs;
